import Menu from "../Menu/Menu";
import Page from "../../pages/Page";
import React, { Component, useEffect } from "react";
import { IonAlert, IonApp, IonRouterOutlet, IonSplitPane } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Route, RouteComponentProps } from "react-router-dom";
import store from "../../store";
import { Provider } from "react-redux";

import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

import "../../theme/variables.scss";
import "./App.scss";
import Authorization from "../Authorization/Authorization";

type AppProps = {};

type AppState = {
  selectedPage: string;
  showInstallPWA: boolean;
};

interface RouteWrapperProps {
  onPageChange: (pageName: string) => void;
  pageName: string;
}

const RouteWrapper: React.FC<RouteComponentProps & RouteWrapperProps> = ({
  onPageChange,
  pageName,
  children,
  ...props
}) => {
  useEffect(() => {
    onPageChange(pageName);
  }, [onPageChange, pageName]);

  return React.cloneElement(children as React.ReactElement, props);
};

const PWAInstallHandler: React.FC<{
  onInstallPrompt: (event: any) => void;
}> = ({ onInstallPrompt }) => {
  useEffect(() => {
    window.addEventListener("beforeinstallprompt", onInstallPrompt);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstallPrompt);
    };
  }, [onInstallPrompt]);

  return null;
};

class App extends Component<AppProps, AppState> {
  private deferredPrompt: any = null;

  constructor(props: AppProps) {
    super(props);
    this.state = {
      selectedPage: "",
      showInstallPWA: false,
    };
  }

  handlePageChange = (pageName: string) => {
    this.setState({ selectedPage: pageName });
  };

  handleInstallPrompt = (event: any) => {
    event.preventDefault();
    this.deferredPrompt = event;
    this.setState({ showInstallPWA: true });
  };

  handleInstall = async () => {
    if (this.deferredPrompt) {
      try {
        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);
      } catch (error) {
        console.warn("PWA installation failed:", error);
      } finally {
        this.deferredPrompt = null;
        this.setState({ showInstallPWA: false });
      }
    }
  };

  handleAlertDismiss = () => {
    this.setState({ showInstallPWA: false });
  };

  renderRoute = (
    props: RouteComponentProps,
    pageName: string,
    Component: React.ComponentType<any>
  ) => (
    <RouteWrapper
      {...props}
      onPageChange={this.handlePageChange}
      pageName={pageName}
    >
      <Component />
    </RouteWrapper>
  );

  render() {
    const { selectedPage, showInstallPWA } = this.state;

    return (
      <Provider store={store}>
        <IonApp>
          <IonReactRouter>
            <IonSplitPane contentId="main">
              <Menu selectedPage={selectedPage} />
              <IonRouterOutlet id="main">
                <Route
                  path="/page/:name"
                  render={(props: RouteComponentProps<{ name: string }>) =>
                    this.renderRoute(props, props.match.params.name, Page)
                  }
                  exact={true}
                />
                <Route
                  path="/"
                  render={(props: RouteComponentProps) =>
                    this.renderRoute(props, "home", Page)
                  }
                  exact={true}
                />
                <Route
                  path="/authorization"
                  render={(props: RouteComponentProps) =>
                    this.renderRoute(props, "authorization", Authorization)
                  }
                />
              </IonRouterOutlet>
            </IonSplitPane>
          </IonReactRouter>

          <PWAInstallHandler onInstallPrompt={this.handleInstallPrompt} />
        </IonApp>

        <IonAlert
          isOpen={showInstallPWA}
          onDidDismiss={this.handleAlertDismiss}
          header="Save Apps"
          message="Save apps on your device?"
          buttons={[
            {
              text: "Cancel",
              role: "cancel",
              cssClass: "secondary",
              handler: this.handleAlertDismiss,
            },
            {
              text: "Ok",
              cssClass: "installPWA",
              handler: this.handleInstall,
            },
          ]}
        />
      </Provider>
    );
  }
}

export default App;
